// Transport auth for Twetch writes.
//
// Every mutating request carries three headers — X-Twetch-User, X-Twetch-Ts,
// X-Twetch-Sig — where the signature is a Bitcoin-Signed-Message by the
// DEVICE session key over a canonical string binding method, path (no
// query), user id, timestamp and the EXACT body bytes transmitted. The
// server reconstructs the same string and recovers the device pubkey; a
// byte of drift anywhere fails auth, so the body is serialized ONCE here and
// both signed and sent as that string.

import { owContextHeaders } from './ow-context-headers'
import { TWETCH_API, TWETCH_BRIDGE_API, TwetchError } from './twetch-api'
import { bsmSign, type DeviceKey } from './twetch-crypto'

/// The canonical request string, exactly as the server rebuilds it.
export function transportCanonical(
	method: string,
	path: string,
	uid: number,
	ts: number,
	body: string
): string {
	const pathOnly = path.split('?')[0]
	return `${method}\n${pathOnly}\n${uid}\n${ts}\n${body}`
}

async function transportHeaders(
	deviceKey: DeviceKey,
	uid: number,
	method: string,
	path: string,
	body: string
): Promise<Record<string, string>> {
	const ts = Date.now()
	const sig = await bsmSign(deviceKey.priv, transportCanonical(method, path, uid, ts, body))
	return {
		'x-twetch-user': String(uid),
		'x-twetch-ts': String(ts),
		'x-twetch-sig': sig
	}
}

/// A signed write. Throws TwetchError with the server's own message on non-2xx.
async function twetchSignedRequest<T>(
	base: string,
	method: 'POST' | 'PATCH',
	path: string,
	body: unknown,
	auth: { uid: number; deviceKey: DeviceKey } | null
): Promise<T> {
	const bodyString = JSON.stringify(body)
	const headers = {
		'content-type': 'application/json',
		accept: 'application/json',
		// Same-origin bridge only: the wallet/build/address context for the
		// server-side rejection line. Never sent to api.twetch.com directly.
		...(base === TWETCH_BRIDGE_API ? owContextHeaders() : {}),
		...(auth ? await transportHeaders(auth.deviceKey, auth.uid, method, path, bodyString) : {})
	}
	const res = await fetch(`${base}${path}`, { method, headers, body: bodyString })
	if (!res.ok) {
		let detail = ''
		try {
			const parsed = (await res.json()) as { error?: string; message?: string }
			detail = parsed?.error || parsed?.message || ''
		} catch {
			// non-JSON error body
		}
		throw new TwetchError(res.status, detail || `HTTP ${res.status} for ${path}`)
	}
	return res.json() as Promise<T>
}

/// A POST through the same-origin bridge (sponsored writes need it).
///
/// `auth` is null for a LINKED wallet, which has no session to sign with —
/// opening one would rotate the account's key. For a sponsored action the
/// wallet's own AIP signature inside the body is the authorization: it names
/// the action and its exact target, the server requires that wallet to own the
/// account, and the bridge is what lets a BTC signer through at all.
export function twetchSignedPost<T>(
	path: string,
	body: unknown,
	auth: { uid: number; deviceKey: DeviceKey } | null
): Promise<T> {
	return twetchSignedRequest<T>(TWETCH_BRIDGE_API, 'POST', path, body, auth)
}

/// A signed POST straight to Twetch — for writes that need only the device
/// key (no sponsorship, so no bridge).
export function twetchSignedPostDirect<T>(
	path: string,
	body: unknown,
	auth: { uid: number; deviceKey: DeviceKey }
): Promise<T> {
	return twetchSignedRequest<T>(TWETCH_API, 'POST', path, body, auth)
}

/// A signed PATCH straight to Twetch — the device key is the whole
/// credential, so profile edits need neither the bridge nor a wallet popup.
export function twetchSignedPatch<T>(
	path: string,
	body: unknown,
	auth: { uid: number; deviceKey: DeviceKey }
): Promise<T> {
	return twetchSignedRequest<T>(TWETCH_API, 'PATCH', path, body, auth)
}
